import { useEffect, useMemo, useRef, useState } from "react";
import {
    Card, Col, Row, Select, Typography, Space, Button, Tag, Empty, Spin, Table, Tour, message,
} from "antd";
import {
    FileTextOutlined, CalendarOutlined, FilterOutlined, EyeOutlined,
    FilePdfOutlined, FileExcelOutlined, BankOutlined, BuildOutlined,
    SearchOutlined, QuestionCircleOutlined, CarOutlined, FileProtectOutlined,
} from "@ant-design/icons";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import Footer from "../../../components/Footer";
import axiosInstance from "../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../variables/constants";
import SDIModal from "../../../components/ui/SDIModal";
import EmissionsNettesCedeesParExerciceEtCategorie from "./components/EmissionsNettesCedeesParExerciceEtCategorie";
import EtatGlobalProductionMensuelDTRFNational from "./components/EtatGlobalProductionMensuelDTRFNational";
import EtatProductionVT from "./components/EtatProductionVT";
import {
    exportEmissionsNettesToExcel, exportEmissionsNettesToPDF,
    exportGUMProductionToExcel, exportGUMProductionToPDF,
    exportVTProductionToExcel, exportVTProductionToPDF,
} from "./utils/ExportUtils";

const { Title, Text } = Typography;
const PRIMARY = "#242166";

// Rôle / périmètre de l'utilisateur connecté
const IS_POOL = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
const USER_ORG = LOGGED_USER?.profil?.organisme;
const USER_COMPAGNIE = USER_ORG?.code
    ? { code: USER_ORG.code, raisonSociale: USER_ORG.raisonSociale || USER_ORG.code }
    : null;

const MONTHS = [
    { value: "01", label: "Janvier" }, { value: "02", label: "Février" },
    { value: "03", label: "Mars" }, { value: "04", label: "Avril" },
    { value: "05", label: "Mai" }, { value: "06", label: "Juin" },
    { value: "07", label: "Juillet" }, { value: "08", label: "Août" },
    { value: "09", label: "Septembre" }, { value: "10", label: "Octobre" },
    { value: "11", label: "Novembre" }, { value: "12", label: "Décembre" },
];
const MONTHS_SHORT = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Aoû", "Sep", "Oct", "Nov", "Déc"];

const generateYears = () => {
    const current = new Date().getFullYear();
    const years = [];
    for (let y = current + 1; y >= 2020; y--) years.push({ value: `${y}`, label: `${y}` });
    return years;
};

const fmtNum = (v) => (v != null && v !== "" ? Number(v).toLocaleString("fr-FR") : "-");
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("fr-FR") : "-");

const EtatsProductionGlobalV2 = () => {
    // Référentiels
    const [categories, setCategories] = useState([]);
    const [compagnies, setCompagnies] = useState([]);
    const [agences, setAgences] = useState([]);

    // Sélection
    const [reportKey, setReportKey] = useState("emissions_nettes");
    const [exercice, setExercice] = useState(null);
    const [mois, setMois] = useState("01");
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedCompagnie, setSelectedCompagnie] = useState(IS_POOL ? null : USER_COMPAGNIE);
    const [selectedAgence, setSelectedAgence] = useState(null);

    // Résultats / état UI
    const [loading, setLoading] = useState(false);
    const [datas, setDatas] = useState([]);
    const [lastCount, setLastCount] = useState(null);
    const [apercu, setApercu] = useState(null); // { kind, data } pour la modale

    const [openTour, setOpenTour] = useState(() => localStorage.getItem("etatsProdV2TourSeen") !== "1");
    const closeTour = () => { setOpenTour(false); localStorage.setItem("etatsProdV2TourSeen", "1"); };

    const refReport = useRef(null);
    const refFilters = useRef(null);
    const refSearch = useRef(null);
    const refDownload = useRef(null);
    // Signature des critères ayant produit `datas` (pour réutiliser sans re-fetch).
    const loadedSigRef = useRef(null);

    useEffect(() => {
        axiosInstance.get(`/api/categorie`)
            .then((res) => setCategories((res.data || []).filter((e) => !e.codeCategorie?.startsWith("V"))))
            .catch((e) => console.log(e));
        axiosInstance.get(`/api/organisme/assurance`).then((res) => setCompagnies(res.data || [])).catch((e) => console.log(e));
        // Liste des bureaux : DTRF uniquement (le STCA reste un filtre réseau, pas un bureau).
        axiosInstance.get(`/api/agence/dtrf`).then((res) => setAgences(res.data || [])).catch((e) => console.log(e));
    }, []);

    // ── Configuration des rapports ──────────────────────────────────────
    // kind: 'emissions' (TPVM, annuel, par compagnie) | 'gum' (mensuel, par réseau/bureau)
    const REPORTS = useMemo(() => ([
        {
            key: "emissions_nettes", label: "Émissions nettes cédées", branche: "TPVM",
            icon: <CarOutlined />, color: "#667eea", kind: "emissions",
            // Filtre compagnie masqué : le tableau liste déjà toutes les compagnies (1 ligne/compagnie).
            needsCategorie: true, needsCompagnie: false,
        },
        {
            key: "etat_global_national", label: "État global production GUM (national)", branche: "GUM",
            icon: <FileTextOutlined />, color: "#10b981", kind: "gum",
            needsMois: true,
        },
        {
            key: "etat_production_bureau", label: "État production GUM par bureau", branche: "GUM",
            icon: <BankOutlined />, color: "#f59e0b", kind: "gum",
            needsMois: true, needsBureau: true,
        },
        {
            key: "etat_production_vt", label: "État de production VT", branche: "VT",
            icon: <FileProtectOutlined />, color: "#8b5cf6", kind: "vt",
            needsMois: true,
        },
    ]), []);

    const report = REPORTS.find((r) => r.key === reportKey) || REPORTS[0];

    const catCode = selectedCategory?.codeCategorie || null;
    const catExportCode = catCode === "M" ? "MOTO" : catCode;

    // Query de périmètre (compagnie pour émissions, réseau pour GUM national)
    const scopeQuery = () => {
        const params = new URLSearchParams();
        if (report.needsCompagnie && IS_POOL && selectedCompagnie?.code) params.append("compagnie", selectedCompagnie.code);
        const qs = params.toString();
        return qs ? `?${qs}` : "";
    };

    const validate = () => {
        if (!exercice) { message.warning("Veuillez sélectionner un exercice (année)."); return false; }
        if (report.needsCategorie && !catCode) { message.warning("Veuillez sélectionner une catégorie."); return false; }
        if (report.needsBureau && !selectedAgence?.codeAgence) { message.warning("Veuillez sélectionner un bureau."); return false; }
        return true;
    };

    const fetchData = async () => {
        if (report.kind === "emissions") {
            const { data } = await axiosInstance.get(
                `/api/production/emissions-nettes-cedees/${catCode}/${exercice}-01${scopeQuery()}`);
            return data || [];
        }
        if (report.key === "etat_global_national") {
            const { data } = await axiosInstance.get(
                `/api/production/etat-global-production-national-gum/${exercice}-${mois}${scopeQuery()}`);
            return data || [];
        }
        if (report.key === "etat_production_bureau") {
            const { data } = await axiosInstance.get(
                `/api/production/etat-global-production-gum/${exercice}-${mois}/${selectedAgence.codeAgence}${scopeQuery()}`);
            return data || [];
        }
        if (report.kind === "vt") {
            const { data } = await axiosInstance.get(
                `/api/production/etat-production-vt/${exercice}-${mois}`);
            return data || [];
        }
        return [];
    };

    // Signature des critères courants : si elle correspond à `datas` déjà chargé,
    // on réutilise les données au lieu de relancer l'appel (lent) backend.
    const currentSig = () => JSON.stringify({
        reportKey, exercice, mois, catCode,
        compagnie: report.needsCompagnie && IS_POOL ? selectedCompagnie?.code || null : null,
        agence: report.needsBureau ? selectedAgence?.codeAgence || null : null,
    });

    const handleAction = async (action) => {
        if (report.disabled) { message.info("Ce rapport sera disponible prochainement."); return; }
        if (!validate()) return;
        setLoading(true);
        try {
            // Réutilise les lignes déjà chargées si les critères n'ont pas changé
            // (sauf « Rechercher » qui rafraîchit toujours).
            let rows;
            const sig = currentSig();
            if (action !== "search" && datas.length && loadedSigRef.current === sig) {
                rows = datas;
            } else {
                const data = await fetchData();
                // Clé de ligne UNIQUE par index : les rapports GUM/VT peuvent avoir des `date`
                // répétées/nulles → clés dupliquées → la pagination AntD se bloque. __rowKey règle ça.
                rows = (Array.isArray(data) ? data : (data ? [data] : []))
                    .map((r, i) => ({ ...r, __rowKey: i }));
                setDatas(rows);
                setLastCount(rows.length);
                loadedSigRef.current = sig;
            }

            if (action === "search") {
                if (!rows.length) message.info("Aucun enregistrement pour ces critères.");
                return;
            }
            if (!rows.length) { message.info("Aucune donnée à exporter pour ces critères."); return; }

            if (action === "apercu") {
                setApercu({ kind: report.kind, data: rows });
                return;
            }
            const moisStr = `${exercice}-${mois}`;
            let ok;
            if (action === "pdf") {
                ok = report.kind === "emissions"
                    ? await exportEmissionsNettesToPDF(rows, catExportCode, exercice)
                    : report.kind === "vt"
                        ? await exportVTProductionToPDF(rows, moisStr)
                        : await exportGUMProductionToPDF(rows, moisStr, report.needsBureau ? selectedAgence?.ville : null);
            } else if (action === "excel") {
                ok = report.kind === "emissions"
                    ? exportEmissionsNettesToExcel(rows, catExportCode, exercice)
                    : report.kind === "vt"
                        ? exportVTProductionToExcel(rows, moisStr)
                        : exportGUMProductionToExcel(rows, moisStr, report.needsBureau ? selectedAgence?.ville : null);
            }
            if (ok === false) message.error("Échec de la génération du fichier. Réessayez.");
            else message.success("Export généré.");
        } catch (e) {
            console.error(e);
            message.error("Erreur lors de la génération du rapport.");
        } finally {
            setLoading(false);
        }
    };

    // ── Colonnes du tableau de résultats ────────────────────────────────
    const emissionsColumns = [
        { title: "Compagnie", key: "comp", fixed: "left", width: 220, render: (_, r) => r.compagnie?.raisonSociale || r.compagnie?.code || "-" },
        ...MONTHS_SHORT.map((m, i) => {
            const field = ["janvier", "fevrier", "mars", "avril", "mai", "juin", "juillet", "aout", "septembre", "octobre", "novembre", "decembre"][i];
            return { title: m, dataIndex: field, key: field, align: "right", render: fmtNum };
        }),
        { title: "Total", dataIndex: "total", key: "total", align: "right", render: (v) => <b>{fmtNum(v)}</b> },
    ];

    const gumColumns = [
        { title: "Date", dataIndex: "date", key: "date", fixed: "left", width: 110, render: fmtDate },
        { title: "Nb P0", dataIndex: "nombreMotoPuissance0", key: "p0", align: "right", render: fmtNum },
        { title: "Nb P1", dataIndex: "nombreMotoPuissance1", key: "p1", align: "right", render: fmtNum },
        { title: "Nb P2", dataIndex: "nombreMotoPuissance2", key: "p2", align: "right", render: fmtNum },
        { title: "Nb P3", dataIndex: "nombreMotoPuissance3", key: "p3", align: "right", render: fmtNum },
        { title: "Nb total", dataIndex: "nombreMotoTotal", key: "nbt", align: "right", render: (v) => <b>{fmtNum(v)}</b> },
        { title: "Prime nette", dataIndex: "primeNette", key: "pn", align: "right", render: fmtNum },
        { title: "Taxes", dataIndex: "taxes", key: "tx", align: "right", render: fmtNum },
        { title: "Prime TTC", dataIndex: "primeTotale", key: "ttc", align: "right", render: (v) => <b>{fmtNum(v)}</b> },
        { title: "Cumul", dataIndex: "cumul", key: "cumul", align: "right", render: fmtNum },
    ];

    // VT : aperçu compact à l'écran (détail complet dans « Aperçu »).
    const vtColumns = [
        { title: "Date", dataIndex: "date", key: "date", fixed: "left", width: 110, render: fmtDate },
        { title: "Nb VL", dataIndex: "nbVoiture", key: "vl", align: "right", render: fmtNum },
        { title: "Nb Camion", dataIndex: "nbCamion", key: "vc", align: "right", render: fmtNum },
        { title: "Total AN", dataIndex: "nbTotalAn", key: "tan", align: "right", render: (v) => <b>{fmtNum(v)}</b> },
        { title: "Total encaissé", dataIndex: "totalEncaisse", key: "te", align: "right", render: (v) => <b>{fmtNum(v)}</b> },
        { title: "Base comm. STAC", dataIndex: "baseCommissionStac", key: "stac", align: "right", render: fmtNum },
        { title: "Report veille", dataIndex: "reportVeille", key: "rv", align: "right", render: fmtNum },
        { title: "Versement", dataIndex: "versementDuJour", key: "vj", align: "right", render: fmtNum },
        { title: "Timbre", dataIndex: "timbre", key: "tb", align: "right", render: fmtNum },
        { title: "Solde à reverser", dataIndex: "soldeAReverser", key: "sar", align: "right", render: (v) => <b>{fmtNum(v)}</b> },
    ];

    const tableColumns = report.kind === "emissions" ? emissionsColumns
        : report.kind === "vt" ? vtColumns : gumColumns;

    // ── Styles ──────────────────────────────────────────────────────────
    const cardStyle = { borderRadius: 14, border: "1px solid #eef0f4", boxShadow: "0 2px 10px rgba(36,33,102,0.05)" };
    const fieldLabel = { display: "flex", alignItems: "center", gap: 6, color: "#6b7280", fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.3 };

    // ── Tour guidé ──────────────────────────────────────────────────────
    const tourSteps = [
        { title: "1. Type de rapport", description: "Choisissez l'état de production à générer (émissions cédées TPVM, GUM national, GUM par bureau).", target: () => refReport.current, nextButtonProps: { children: "Suivant" } },
        { title: "2. Composer les filtres", description: `Sélectionnez l'exercice${report.needsMois ? ", le mois" : ""}${report.needsCategorie ? ", la catégorie" : ""}${IS_POOL && report.needsCompagnie ? ", la compagnie" : ""}${report.needsBureau ? ", le bureau" : ""}.`, target: () => refFilters.current, nextButtonProps: { children: "Suivant" }, prevButtonProps: { children: "Précédent" } },
        { title: "3. Rechercher", description: "Cliquez sur « Rechercher » pour afficher les lignes dans le tableau.", target: () => refSearch.current, nextButtonProps: { children: "Suivant" }, prevButtonProps: { children: "Précédent" } },
        { title: "4. Aperçu & téléchargement", description: "« Aperçu » ouvre l'état mis en forme ; « PDF » et « Excel » téléchargent le rapport.", target: () => refDownload.current, nextButtonProps: { children: "Terminer" }, prevButtonProps: { children: "Précédent" } },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper" style={{ background: "#f6f7fb" }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                                <div>
                                    <Title level={3} style={{ margin: 0, color: PRIMARY }}>
                                        <FileTextOutlined style={{ marginRight: 10 }} /> États de production
                                    </Title>
                                    <Text type="secondary">Sélectionnez un rapport, affinez les filtres, puis prévisualisez ou téléchargez (PDF / Excel).</Text>
                                </div>
                                <Button icon={<QuestionCircleOutlined />} onClick={() => setOpenTour(true)}>Tutoriel</Button>
                            </div>

                            {/* Sélecteur de rapport (chips) */}
                            <Card ref={refReport} style={{ ...cardStyle, marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
                                <div style={fieldLabel}><FilterOutlined /> Type de rapport</div>
                                <Row gutter={[12, 12]}>
                                    {REPORTS.map((r) => {
                                        const active = r.key === reportKey;
                                        return (
                                            <Col xs={24} sm={12} lg={6} key={r.key}>
                                                <div
                                                    onClick={() => { if (r.disabled) { message.info("Bientôt disponible."); return; } setReportKey(r.key); setDatas([]); setLastCount(null); }}
                                                    style={{
                                                        cursor: r.disabled ? "not-allowed" : "pointer", borderRadius: 12, padding: "12px 14px",
                                                        border: `1.5px solid ${active ? r.color : "#eef0f4"}`,
                                                        background: active ? `${r.color}10` : "#fff",
                                                        opacity: r.disabled ? 0.55 : 1,
                                                        display: "flex", alignItems: "center", gap: 12, transition: "all .2s",
                                                    }}
                                                >
                                                    <span style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: `${r.color}1a`, color: r.color, fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>{r.icon}</span>
                                                    <span style={{ fontWeight: 600, color: active ? PRIMARY : "#374151", fontSize: 13 }}>
                                                        {r.label}{r.disabled && <Tag style={{ marginLeft: 6 }} color="default">à venir</Tag>}
                                                    </span>
                                                </div>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </Card>

                            {/* Filtres */}
                            <Card ref={refFilters} style={{ ...cardStyle, marginBottom: 16 }} bodyStyle={{ padding: 18 }}>
                                <Row gutter={[16, 16]} align="bottom">
                                    <Col xs={24} sm={12} md={5}>
                                        <div style={fieldLabel}><CalendarOutlined /> Exercice</div>
                                        <Select
                                            placeholder="Année" style={{ width: "100%" }}
                                            value={exercice || undefined} onChange={setExercice}
                                            options={generateYears()}
                                        />
                                    </Col>

                                    {report.needsMois && (
                                        <Col xs={24} sm={12} md={5}>
                                            <div style={fieldLabel}><CalendarOutlined /> Mois</div>
                                            <Select style={{ width: "100%" }} value={mois} onChange={setMois} options={MONTHS} />
                                        </Col>
                                    )}

                                    {report.needsCategorie && (
                                        <Col xs={24} sm={12} md={5}>
                                            <div style={fieldLabel}><FileTextOutlined /> Catégorie</div>
                                            <Select
                                                placeholder="Sélectionner" style={{ width: "100%" }}
                                                value={catCode || undefined}
                                                onChange={(_, opt) => setSelectedCategory(opt?.data || null)}
                                                options={[
                                                    // Branches groupées (le backend gère MOTO='M', TPV=A/B/T).
                                                    { value: "MOTO", label: "MOTO", data: { codeCategorie: "MOTO", nom: "MOTO" } },
                                                    { value: "TPV", label: "TPV", data: { codeCategorie: "TPV", nom: "TPV" } },
                                                ]}
                                            />
                                        </Col>
                                    )}

                                    {report.needsCompagnie && (
                                        <Col xs={24} sm={12} md={6}>
                                            <div style={fieldLabel}><BuildOutlined /> Compagnie {!IS_POOL && <Tag color="default">verrouillée</Tag>}</div>
                                            <Select
                                                showSearch optionFilterProp="label" allowClear={IS_POOL} disabled={!IS_POOL}
                                                placeholder={IS_POOL ? "Toutes les compagnies" : undefined}
                                                style={{ width: "100%" }}
                                                value={selectedCompagnie?.code || undefined}
                                                onChange={(_, opt) => setSelectedCompagnie(opt?.data || null)}
                                                options={(IS_POOL ? compagnies : (USER_COMPAGNIE ? [USER_COMPAGNIE] : []))
                                                    .map((c) => ({ value: c.code, label: c.raisonSociale, data: c }))}
                                            />
                                        </Col>
                                    )}

                                    {report.needsBureau && (
                                        <Col xs={24} sm={12} md={6}>
                                            <div style={fieldLabel}><BankOutlined /> Bureau</div>
                                            <Select
                                                showSearch optionFilterProp="label" allowClear placeholder="Sélectionner le bureau"
                                                style={{ width: "100%" }}
                                                value={selectedAgence?.codeAgence || undefined}
                                                onChange={(_, opt) => setSelectedAgence(opt?.data || null)}
                                                options={agences.map((a) => ({ value: a.codeAgence, label: a.nom, data: a }))}
                                            />
                                        </Col>
                                    )}
                                </Row>
                            </Card>

                            {/* Actions + résultat */}
                            <Card style={cardStyle} bodyStyle={{ padding: 18 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                                    <Space size={10} wrap>
                                        <Tag color={report.color} style={{ padding: "4px 10px", borderRadius: 8, fontWeight: 600 }}>{report.label}</Tag>
                                        {exercice && <Text type="secondary">{report.needsMois ? `${MONTHS.find((m) => m.value === mois)?.label} ${exercice}` : `Année ${exercice}`}</Text>}
                                        {lastCount != null && <Tag color="default">{lastCount} ligne(s)</Tag>}
                                    </Space>

                                    <Space size={10} wrap>
                                        <Button ref={refSearch} type="primary" icon={<SearchOutlined />} loading={loading}
                                            onClick={() => handleAction("search")}
                                            style={{ background: PRIMARY, height: 42, borderRadius: 8, fontWeight: 600 }}>
                                            Rechercher
                                        </Button>
                                        <Button ref={refDownload} icon={<EyeOutlined />} loading={loading}
                                            onClick={() => handleAction("apercu")}
                                            style={{ height: 42, borderRadius: 8, fontWeight: 600, color: PRIMARY, borderColor: PRIMARY }}>
                                            Aperçu
                                        </Button>
                                        <Button icon={<FilePdfOutlined />} loading={loading} onClick={() => handleAction("pdf")}
                                            style={{ height: 42, borderRadius: 8, fontWeight: 600, color: "#1890ff", borderColor: "#1890ff" }}>
                                            PDF
                                        </Button>
                                        <Button icon={<FileExcelOutlined />} loading={loading} onClick={() => handleAction("excel")}
                                            style={{ height: 42, borderRadius: 8, fontWeight: 600, color: "#217346", borderColor: "#217346" }}>
                                            Excel
                                        </Button>
                                    </Space>
                                </div>

                                <div style={{ marginTop: 16 }}>
                                    {loading ? (
                                        <div style={{ textAlign: "center", padding: 40 }}>
                                            <Spin size="large" />
                                            <div style={{ marginTop: 12, color: "#6b7280" }}>Chargement du rapport…</div>
                                        </div>
                                    ) : datas.length > 0 ? (
                                        <Table
                                            size="small"
                                            rowKey={(r) => r.__rowKey ?? r.code_id ?? r.codeId ?? r.date ?? r.compagnie?.code}
                                            columns={tableColumns}
                                            dataSource={datas}
                                            scroll={{ x: "max-content" }}
                                            pagination={{ defaultPageSize: 15, showSizeChanger: true, pageSizeOptions: ["10", "15", "30", "50", "100"], showTotal: (t) => `${t} ligne(s)` }}
                                            summary={datas.length > 0
                                                ? () => {
                                                    // Ligne de totaux en bas — somme sur TOUTES les lignes (grand total),
                                                    // pas seulement la page courante. Générique (émissions / GUM / VT).
                                                    // On N'ADDITIONNE PAS les colonnes cumulatives / de report (le « cumul »
                                                    // GUM et le « report veille » VT sont des soldes courants, pas additionnables).
                                                    const NO_SUM_KEYS = new Set(["cumul", "rv"]);
                                                    return (
                                                        <Table.Summary fixed>
                                                            <Table.Summary.Row style={{ background: "#f7f8fb" }}>
                                                                {tableColumns.map((col, idx) => {
                                                                    if (idx === 0) {
                                                                        return (
                                                                            <Table.Summary.Cell key="total-label" index={0}>
                                                                                <b>TOTAL</b>
                                                                            </Table.Summary.Cell>
                                                                        );
                                                                    }
                                                                    const di = col.dataIndex;
                                                                    const summable = di && !NO_SUM_KEYS.has(col.key)
                                                                        && datas.some((r) => typeof r[di] === "number");
                                                                    if (!summable) {
                                                                        return <Table.Summary.Cell key={col.key || idx} index={idx} align="right" />;
                                                                    }
                                                                    const sum = datas.reduce((s, r) => s + (Number(r[di]) || 0), 0);
                                                                    return (
                                                                        <Table.Summary.Cell key={col.key || idx} index={idx} align="right">
                                                                            <b>{fmtNum(sum)}</b>
                                                                        </Table.Summary.Cell>
                                                                    );
                                                                })}
                                                            </Table.Summary.Row>
                                                        </Table.Summary>
                                                    );
                                                }
                                                : undefined}
                                        />
                                    ) : (
                                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description={<Text type="secondary">Cliquez sur « Rechercher » pour afficher les lignes, puis « Aperçu » / PDF / Excel.</Text>} />
                                    )}
                                </div>
                            </Card>

                            {/* Aperçu mis en forme (état imprimable) */}
                            <SDIModal
                                width={apercu?.kind === "vt" ? "98vw" : report.kind === "emissions" ? 1900 : 1400}
                                open={!!apercu}
                                onCancel={() => setApercu(null)}
                                title={`Aperçu — ${report.label}`}
                                zIndex={10000}
                            >
                                {apercu?.kind === "emissions" && (
                                    <EmissionsNettesCedeesParExerciceEtCategorie
                                        emissions={apercu.data}
                                        categorie={catExportCode}
                                        exercice={exercice}
                                    />
                                )}
                                {apercu?.kind === "gum" && (
                                    <EtatGlobalProductionMensuelDTRFNational
                                        jours={apercu.data}
                                        mois={`${exercice}-${mois}`}
                                        bureau={report.needsBureau ? selectedAgence?.ville : undefined}
                                    />
                                )}
                                {apercu?.kind === "vt" && (
                                    <div style={{ overflowX: "auto" }}>
                                        <EtatProductionVT data={apercu.data} mois={`${exercice}-${mois}`} />
                                    </div>
                                )}
                            </SDIModal>

                            <Tour open={openTour} onClose={closeTour} steps={tourSteps} />

                            <Footer />
                            <div className="content-backdrop fade"></div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default EtatsProductionGlobalV2;
